import { Injectable } from '@angular/core';
import { TopCategory } from '@shared/models';
import {
  PurchaseCredit,
  PurchaseHistory,
  PurchaseInvoice,
  RefundDetails,
  RefundHistory,
  RefundResponse,
} from '@shared/models/purchase-history.model';
import {
  CourseDetails,
  CoursesInstructor,
  EditorImageInfo,
  ProductReviewsResponse,
  Section,
  ZeroPaymentModal,
} from '@shared/store/courses';
import { Observable } from 'rxjs';
import {
  API_BEST_SALLER_COURSE,
  API_CART_RECENTLY_WISHLISTED,
  API_COURSE_DETAILS_COURSES_CONTENT,
  API_COURSE_DETAILS_COURSES_FAVORITE_TOGGLE,
  API_COURSE_DETAILS_COURSES_PRICE,
  API_COURSE_DETAILS_COURSES_REVIEW_POST,
  API_COURSE_DETAILS_COURSES_REVIEWS,
  API_COURSE_DETAILS_INFO_INSTRUCTOR_PREVIEW,
  API_COURSE_DETAILS_INSTRUCTOR_INFO,
  API_COURSE_DETAILS_PREVIEW,
  API_COURSE_DETAILS_REVIEW_PREVIEW,
  API_COURSE_DETAILS_SECTION_PREVIEW,
  API_COURSES_DETAILS_GET_PUBLIC,
  API_GET_COURSE_DETAILS_LECTURE_VIDEO,
  API_GET_COURSE_DETAILS_PROMO_VIDEO,
  API_GET_PREVIEW_COURSE_DETAILS_LECTURE_VIDEO,
  API_GET_PREVIEW_COURSE_DETAILS_PROMO_VIDEO,
  API_GET_PURCHASE_CREDIT_NOTE,
  API_GET_PURCHASE_DETAILS,
  API_GET_PURCHASE_HISTORY,
  API_GET_PURCHASE_INVOICE,
  API_GET_PURCHASE_RECEIPT,
  API_GET_REFUND_REQUEST,
  API_GET_REFUND_VALIDATE,
  API_HOME_TOP_CATEGORIES,
  API_MOST_POPULAR_COURSES,
  API_POST_EDITOR_IMAGE_UPLOAD,
  API_POST_SUBMIT_REFUND_REQUEST,
  API_POST_SUBSCRIBE_NEWSLETTER,
  API_POST_ZERO_PAYMENT,
  API_PUBLISHED_COURSES,
  Course,
  CourseListPayload,
  encodeURL,
  HttpService,
  Logger,
  Pagination,
  UtilsService,
} from 'thkee-common';
const log = new Logger('CoursesService');

export interface IsEligible {
  is_eligible: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CoursesDetailsService {
  constructor(private readonly http: HttpService, private utilsService: UtilsService) {}

  // Course details
  getCourseData(courseId: string): Observable<CourseDetails> {
    return this.http.get<CourseDetails>(API_COURSES_DETAILS_GET_PUBLIC.replace('<COURSE_ID>', courseId));
  }

  getCourseInstructorInfo(slug: string): Observable<CoursesInstructor[]> {
    return this.http.get<CoursesInstructor[]>(API_COURSE_DETAILS_INSTRUCTOR_INFO.replace('<SLUG>', slug));
  }

  getCoursesPrice(params = {} as object): Observable<any> {
    return this.http.get<any>(encodeURL(API_COURSE_DETAILS_COURSES_PRICE, params));
  }

  getCoursesContent(slug: string): Observable<any> {
    return this.http.get<any>(API_COURSE_DETAILS_COURSES_CONTENT.replace('<SLUG>', slug));
  }

  getCoursesReviews(slug: string): Observable<ProductReviewsResponse> {
    return this.http.get<ProductReviewsResponse>(API_COURSE_DETAILS_COURSES_REVIEWS.replace('<SLUG>', slug));
  }

  postCoursesReviews(course__pk: string, body: any): Observable<any> {
    return this.http.post<any>(API_COURSE_DETAILS_COURSES_REVIEW_POST.replace('<COURSE__PK>', course__pk), body);
  }

  coursesFavoriteToggle(body = {} as object): Observable<any> {
    return this.http.post<any>(API_COURSE_DETAILS_COURSES_FAVORITE_TOGGLE, body);
  }

  // Category page
  getBestSellerCourse(params = {} as object): Observable<CourseDetails> {
    return this.http.get<CourseDetails>(encodeURL(API_BEST_SALLER_COURSE, params));
  }

  // Home page
  getCourses(params = {} as CourseListPayload): Observable<Pagination<Partial<Course>>> {
    return this.http.get<Pagination<Partial<Course>>>(encodeURL(API_PUBLISHED_COURSES, params));
  }

  getHomeTopCategories(params = {} as CourseListPayload): Observable<TopCategory[]> {
    return this.utilsService.get(encodeURL(API_HOME_TOP_CATEGORIES, params));
  }

  getPopularCourses(params = {} as CourseListPayload): Observable<Pagination<Partial<Course>>> {
    return this.http.get<Pagination<Partial<Course>>>(encodeURL(API_MOST_POPULAR_COURSES, params));
  }

  // Purchase history
  getPurchaseHistory(params = {} as CourseListPayload): Observable<PurchaseHistory> {
    return this.http.get<PurchaseHistory>(encodeURL(API_GET_PURCHASE_HISTORY, params));
  }

  getRefundeHistory(params = {} as CourseListPayload): Observable<RefundHistory> {
    return this.http.get<RefundHistory>(encodeURL(API_GET_REFUND_REQUEST, params));
  }

  getPurchaseInvoice(id: string): Observable<PurchaseInvoice> {
    return this.http.get<PurchaseInvoice>(API_GET_PURCHASE_INVOICE.replace('<ID>', id));
  }

  getPurchaseReceipt(id: string): Observable<PurchaseInvoice> {
    return this.http.get<PurchaseInvoice>(API_GET_PURCHASE_RECEIPT.replace('<ID>', id));
  }

  checkRefundAvailablity(params = {} as Object): Observable<IsEligible> {
    return this.utilsService.get(encodeURL(API_GET_REFUND_VALIDATE, params));
  }

  getRefundDetails(params = {} as Object): Observable<RefundDetails> {
    return this.http.get<RefundDetails>(encodeURL(API_GET_PURCHASE_DETAILS, params));
  }

  postRefundRequest(body = {} as object): Observable<RefundResponse> {
    return this.http.post<RefundResponse>(API_POST_SUBMIT_REFUND_REQUEST, body);
  }

  getPurchaseCreditNote(id: string): Observable<PurchaseCredit> {
    return this.http.get<PurchaseCredit>(API_GET_PURCHASE_CREDIT_NOTE.replace('<ID>', id));
  }

  // Cart
  getRecentlyWishlisted(params = {} as CourseListPayload): Observable<Pagination<Partial<Course>>> {
    return this.http.get<Pagination<Partial<Course>>>(encodeURL(API_CART_RECENTLY_WISHLISTED, params));
  }

  // Preview course
  getPreviewCoursesDetails(slug: string): Observable<CourseDetails> {
    return this.http.get<CourseDetails>(API_COURSE_DETAILS_PREVIEW.replace('<SLUG>', slug));
  }
  getPreviewCourseInstructorInfo(slug: string): Observable<CoursesInstructor[]> {
    return this.http.get<CoursesInstructor[]>(API_COURSE_DETAILS_INFO_INSTRUCTOR_PREVIEW.replace('<SLUG>', slug));
  }
  getPreviewCourseContent(slug: string): Observable<Section[]> {
    return this.http.get<Section[]>(API_COURSE_DETAILS_SECTION_PREVIEW.replace('<SLUG>', slug));
  }
  getPreviewCoursesReviews(slug: string): Observable<ProductReviewsResponse> {
    return this.http.get<ProductReviewsResponse>(API_COURSE_DETAILS_REVIEW_PREVIEW.replace('<SLUG>', slug));
  }

  // Free course
  buyFreeCourse(body = {} as object): Observable<ZeroPaymentModal> {
    return this.http.post<ZeroPaymentModal>(API_POST_ZERO_PAYMENT, body);
  }

  // Get transcoded video
  getCoursesPromoVideoTranscodedInfo(slug: string): Observable<any> {
    return this.http.get<any>(API_GET_COURSE_DETAILS_PROMO_VIDEO.replace('<SLUG>', slug));
  }

  getPreviewCoursesPromoVideoTranscodedInfo(slug: string): Observable<any> {
    return this.http.get<any>(API_GET_PREVIEW_COURSE_DETAILS_PROMO_VIDEO.replace('<SLUG>', slug));
  }

  getCoursesLectureVideoTranscodedInfo(slug: string, lectureId: string): Observable<any> {
    return this.http.get<any>(
      API_GET_COURSE_DETAILS_LECTURE_VIDEO.replace('<SLUG>', slug).replace('<LECTURE_ID>', lectureId)
    );
  }

  getPreviewCoursesLectureVideoTranscodedInfo(lectureId: string): Observable<any> {
    return this.http.get<any>(API_GET_PREVIEW_COURSE_DETAILS_LECTURE_VIDEO.replace('<LECTURE_ID>', lectureId));
  }

  // Editor file upload
  uploadImage(file: File): Observable<EditorImageInfo> {
    const formData = new FormData();
    formData.append('image', file);
    return this.http.post<EditorImageInfo>(API_POST_EDITOR_IMAGE_UPLOAD, formData);
  }

  subscribeNewsletter(body = {} as object): Observable<Object> {
    return this.http.post<Object>(API_POST_SUBSCRIBE_NEWSLETTER, body);
  }
}
